import { useLocalStorageValue, useSessionStorageValue } from "@react-hookz/web";
import type {
  UseStorageValueOptions,
  UseStorageValueResult,
} from "@react-hookz/web/dist/useStorageValue";
import { useCallback, useMemo } from "react";

// Prevent It treated as JSX and make the transpiler throw an error
export function useFallbackStorageValue<T>(
  key: string,
  options?: UseStorageValueOptions<T, boolean | undefined>,
) {
  const {
    fetch: LocalStorageFetch,
    remove: localStorageRemove,
    set: localStorageSet,
    value: localStorageValue,
  } = useLocalStorageValue<T>(key, options);
  const {
    fetch: sessionStorageFetch,
    remove: sessionStorageRemove,
    set: sessionStorageSet,
    value: sessionStorageValue,
  } = useSessionStorageValue<T>(key, options);

  const handleSet = useCallback(
    (parameter: Parameters<typeof localStorageSet>[0]) => {
      try {
        localStorageSet(parameter);
      } catch {
        sessionStorageSet(parameter);
      }
    },
    [localStorageSet, sessionStorageSet],
  );

  const handleFetch = useCallback(() => {
    LocalStorageFetch();
    sessionStorageFetch();
  }, [LocalStorageFetch, sessionStorageFetch]);

  const handleRemove = useCallback(() => {
    localStorageRemove();
    sessionStorageRemove();
  }, [localStorageRemove, sessionStorageRemove]);

  const wrappedValue = useMemo(
    () => localStorageValue ?? sessionStorageValue,
    [localStorageValue, sessionStorageValue],
  );

  return {
    fetch: handleFetch,
    remove: handleRemove,
    set: handleSet,
    value: wrappedValue,
  } as UseStorageValueResult<T>;
}
